import React, { useEffect, useState, useCallback } from 'react';
import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

function InterviewPostForm() {
  const navigate = useNavigate();
  // ...existing code...
  const [redirectPostId, setRedirectPostId] = useState(null);
  const DRAFT_KEY = "interviewPostDraft";
  const [formData, setFormData] = useState({
    companyName: '',
    roleName: '',
    numRounds: '',
    startDate: '',
    endDate: '',
    behavioralQuestions: '',
    technicalQuestions: '',
    rating: '',
    offerReceived: '', // IMPORTANT: keep as string to match select values
    additionalInfo: '',
    username: '', // Always attach username to post
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const validate = useCallback((data) => {
    const newErrors = {};

    if (!data.companyName.trim()) newErrors.companyName = 'Company name is required.';
    if (!data.roleName.trim()) newErrors.roleName = 'Role name is required.';

    if (!data.numRounds) newErrors.numRounds = 'Please select number of rounds.';
    if (!data.rating) newErrors.rating = 'Please select a rating.';
    if (!data.offerReceived) newErrors.offerReceived = 'Please select Yes or No.';

    if (!data.startDate) newErrors.startDate = 'Start date is required.';
    if (!data.endDate) newErrors.endDate = 'End date is required.';
    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      newErrors.endDate = 'End date cannot be earlier than start date.';
    }
    if (!data.behavioralQuestions.trim()) {
      newErrors.behavioralQuestions = 'Please add behavioral questions.';
    }
    if (!data.technicalQuestions.trim()) {
      newErrors.technicalQuestions = 'Please add technical questions.';
    }
    if (!user || !user.uid) {
      newErrors.username = 'You must be logged in to post.';
    }
    if (!formData.username || formData.username.trim().length < 3) {
      newErrors.username = 'A valid username is required to post.';
    }
    return newErrors;
  }, [user, formData.username]);

  // load draft once on mount
  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData((prev) => ({ ...prev, ...parsed }));
      } catch (e) {
        localStorage.removeItem(DRAFT_KEY);
      }
    }
  }, []);

  // save draft whenever formData changes
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));

    // real-time validation whenever data changes
    setErrors(validate(formData));
  }, [formData, validate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Fetch username from users collection
        try {
          const res = await import('firebase/firestore');
          const { doc, getDoc } = res;
          const userRef = doc(db, 'users', currentUser.uid);
          const snap = await getDoc(userRef);
          if (snap.exists() && snap.data().username) {
            setFormData((prev) => ({ ...prev, username: snap.data().username }));
          } else {
            setFormData((prev) => ({ ...prev, username: '' }));
          }
        } catch (e) {
          setFormData((prev) => ({ ...prev, username: '' }));
        }
      } else {
        setFormData((prev) => ({ ...prev, username: '' }));
      }
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updated = {
      ...formData,
      [name]: value
    };

    setFormData(updated);
    // errors will also update via useEffect above, but keeping it immediate is okay:
    setErrors(validate(updated));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const showError = (fieldName) => {
    return touched[fieldName] && errors[fieldName];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!user) {
      setError('You must be logged in to post.');
      return;
    }
    // ensure required fields need to be filled out before submission
    if (!formData.companyName.trim()) {
      setError('Company Name is required.');
      return;
    }
    if (!formData.roleName.trim()) {
      setError('Role Name is required.');
      return;
    }
    if (!formData.numRounds) {
      setError('Number of Interview Rounds is required.');
      return;
    }
    if (!formData.rating) {
      setError('Experience Rating is required.');
      return;
    }
    if (!formData.startDate) {
      setError('Interview Start Date is required.');
      return;
    }
    if (!formData.endDate) {
      setError('Interview End Date is required.');
      return;
    }
    // ensures start date is not in the future and is before end date
    const today = new Date();
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    // Remove time for comparison
    today.setHours(0,0,0,0);
    startDate.setHours(0,0,0,0);
    endDate.setHours(0,0,0,0);
    if (startDate > today) {
      setError('Interview Start Date cannot be in the future.');
      return;
    }
    if (startDate > endDate) {
      setError('Interview Start Date must be before or the same day as Interview End Date.');
      return;
    }
    if (!formData.behavioralQuestions.trim()) {
      setError('Behavioral Questions Asked is required.');
      return;
    }
    if (!formData.technicalQuestions.trim()) {
      setError('Technical Questions Asked is required.');
      return;
    }
    if (!formData.offerReceived) {
      setError('Offer Received is required.');
      return;
    }

    const submitErrors = validate(formData);
    setErrors(submitErrors);

    // mark required fields as touched so errors show on submit
    setTouched({
      companyName: true,
      roleName: true,
      numRounds: true,
      rating: true,
      startDate: true,
      endDate: true,
      behavioralQuestions: true,
      technicalQuestions: true,
      offerReceived: true,
    });

    if (Object.keys(submitErrors).length > 0) {
      return;
    }

    try {
      // Always attach username to post
      const postData = {
        ...formData,
        username: formData.username,
        userId: user.uid,
        createdAt: serverTimestamp(),
      };
      const newPost = await addDoc(collection(db, 'posts'), postData);
      setSuccess('Interview experience posted successfully! Redirecting...');
      setShowPopup(true);
      setRedirectPostId(newPost.id);
      setFormData({
        companyName: '',
        roleName: '',
        numRounds: '',
        startDate: '',
        endDate: '',
        behavioralQuestions: '',
        technicalQuestions: '',
        rating: '',
        offerReceived: '',
        additionalInfo: '',
        username: formData.username,
      });

      setTouched({});
      setErrors({});
    } catch (error) {
      setError('Error posting interview experience: ' + error.message);
    }
  };

  // Effect to handle delayed redirect
  useEffect(() => {
    if (showPopup && redirectPostId) {
      const timer = setTimeout(() => {
        navigate(`/post/${redirectPostId}`);
        setShowPopup(false);
        setRedirectPostId(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showPopup, redirectPostId, navigate]);

  return (
    <div className="interview-post-container">
      <h1>Post Your Interview Experience</h1>
      {/* error + success messages for post development */}
      {error && <p style={{color: 'red'}}>{error}</p>}
      {showPopup && (
        <div className="popup-bubble-overlay">
          <div className="popup-bubble">
            {success}
          </div>
        </div>
      )}
      {!user && <p style={{color: 'red'}}>You must be logged in to post.</p>}
      <form onSubmit={handleSubmit} className="interview-form">
        {/* Row 1: Company and Role */}
        <div className="form-row-horizontal">
          <div className="form-group-horizontal">
            <label>Company Name *</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., Google, Microsoft, Amazon"
              aria-invalid={!!showError('companyName')}
            />
            {showError('companyName') && <p className="error-text">{errors.companyName}</p>}
          </div>

          <div className="form-group-horizontal">
            <label>Role Name *</label>
            <input
              type="text"
              name="roleName"
              value={formData.roleName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g., Software Engineer Intern"
              aria-invalid={!!showError('roleName')}
            />
            {showError('roleName') && <p className="error-text">{errors.roleName}</p>}
          </div>
        </div>

        {/* Row 2: Number of Rounds and Rating */}
        <div className="form-row-horizontal">
          <div className="form-group-horizontal">
            <label>Number of Interview Rounds *</label>
            <select
              name="numRounds"
              value={formData.numRounds}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!showError('numRounds')}
            >
              <option value="">Select number of rounds</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10+">10+</option>
            </select>
            {showError('numRounds') && <p className="error-text">{errors.numRounds}</p>}
          </div>

          <div className="form-group-horizontal">
            <label>Experience Rating *</label>
            <select
              name="rating"
              value={formData.rating}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!showError('rating')}
            >
              <option value="">Select rating</option>
              <option value="1">1 - Poor Experience</option>
              <option value="2">2 - Below Average</option>
              <option value="3">3 - Average</option>
              <option value="4">4 - Good Experience</option>
              <option value="5">5 - Excellent Experience</option>
            </select>
            {showError('rating') && <p className="error-text">{errors.rating}</p>}
          </div>
        </div>

        {/* Row 3: Timeline */}
        <div className="form-row-horizontal">
          <div className="form-group-horizontal">
            <label>Interview Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!showError('startDate')}
            />
            {showError('startDate') && <p className="error-text">{errors.startDate}</p>}
          </div>

          <div className="form-group-horizontal">
            <label>Interview End Date *</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!showError('endDate')}
            />
            {showError('endDate') && <p className="error-text">{errors.endDate}</p>}
          </div>
        </div>

        {/* Row 4: Behavioral Questions */}
        <div className="form-group-textarea">
          <label>Behavioral Questions Asked *</label>
          <textarea
            name="behavioralQuestions"
            value={formData.behavioralQuestions}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Describe the behavioral questions you were asked during the interview process..."
            rows="5"
            aria-invalid={!!showError('behavioralQuestions')}
          />
          {showError('behavioralQuestions') && (
            <p className="error-text">{errors.behavioralQuestions}</p>
          )}
        </div>

        {/* Row 5: Technical Questions */}
        <div className="form-group-textarea">
          <label>Technical Questions Asked *</label>
          <textarea
            name="technicalQuestions"
            value={formData.technicalQuestions}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Describe the technical questions you were asked during the interview process..."
            rows="5"
            aria-invalid={!!showError('technicalQuestions')}
          />
          {showError('technicalQuestions') && (
            <p className="error-text">{errors.technicalQuestions}</p>
          )}
        </div>

        {/* Row 6: Offer Received */}
        <div className="form-group-horizontal">
          <label>Offer Received *</label>
          <select
            name="offerReceived"
            value={formData.offerReceived}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!showError('offerReceived')}
          >
            <option value="">Did you receive an offer?</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          {showError('offerReceived') && <p className="error-text">{errors.offerReceived}</p>}
        </div>

        {/* Row 7: Additional Information */}
        <div className="form-group-textarea">
          <label>Additional Information (Optional)</label>
          <textarea
            name="additionalInfo"
            value={formData.additionalInfo}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Any other details you'd like to share about your interview experience..."
            rows="4"
          />
        </div>

        {/* Submit Button */}
        <div className="form-submit">
          <button type="submit">[post interview experience]</button>
        </div>
      </form>
    </div>
  );
}

export default InterviewPostForm;
